import React, { createContext, useMemo, useCallback, useContext } from 'react'
import effects from 'app/effects/effects'
import { ConfirmationBuilder } from 'app/components/confirmation/Confirmation'
import * as R from 'ramda'
import {
  CHANNEL_SMS,
  CHANNEL_EMAIL,
  CHANNEL_APP,
  TWITTER_VALUE,
  FACEBOOK_VALUE,
  INSTAGRAM_VALUE,
  LINKEDIN_VALUE,
} from 'app/constants/channel'

const NotificationActionProvider = ({
  updateNotificationInTimeline,
  ...rest
}) => {
  const { withConfirmation } = useContext(ConfirmationBuilder)

  const bodyText = (notificationChannels) => {
    const withoutAnySocial = !R.includes(notificationChannels, [
      TWITTER_VALUE,
      FACEBOOK_VALUE,
      INSTAGRAM_VALUE,
      LINKEDIN_VALUE,
    ])

    if (
      notificationChannels.length === 1 &&
      notificationChannels === CHANNEL_APP
    )
      return 'notificationTimeline.card.unsend.confirm.body1'
    else if (
      notificationChannels === CHANNEL_APP &&
      R.includes(notificationChannels, [CHANNEL_SMS, CHANNEL_EMAIL]) &&
      withoutAnySocial
    )
      return 'notificationTimeline.card.unsend.confirm.body2'
    else if (
      R.includes(notificationChannels, [
        CHANNEL_APP,
        CHANNEL_SMS,
        CHANNEL_EMAIL,
        TWITTER_VALUE,
        FACEBOOK_VALUE,
        INSTAGRAM_VALUE,
        LINKEDIN_VALUE,
      ])
    )
      return 'notificationTimeline.card.unsend.confirm.body3'
  }

  const withRefreshContent = R.curry((notification, promise) =>
    promise
      .then(() => effects.apis.getNotificationById(R.prop('id', notification)))
      .then(updateNotificationInTimeline),
  )

  const cancelReminder = useCallback(
    (notification) => {
      withConfirmation({
        effectFunc: () =>
          withRefreshContent(
            notification,
            effects.apis.cancelReminder(R.prop('id', notification)),
          ),
        successMsgId: 'notificationTimeline.card.cancelReminder.success',
        titleId: 'notificationTimeline.card.cancelReminder.confirm.title',
        bodyId: 'notificationTimeline.card.cancelReminder.confirm.body',
      })
    },
    [withConfirmation, withRefreshContent],
  )

  const unsend = useCallback(
    (notification) => {
      withConfirmation({
        effectFunc: () => {
          const notificationId = R.prop('id', notification)
          return withRefreshContent(
            notification,
            effects.apis.unsendNotification(notificationId),
          )
        },
        successMsgId: 'notificationTimeline.card.unsend.success',
        titleId: 'notificationTimeline.card.unsend.confirm.title',
        bodyId: bodyText(R.prop('channels', notification)),
      })
    },
    [withConfirmation, withRefreshContent],
  )

  const cancelScheduleNotification = useCallback(
    (notification) => {
      withConfirmation({
        effectFunc: () =>
          withRefreshContent(
            notification,
            effects.apis.cancelScheduleNotification(R.prop('id', notification)),
          ),
        successMsgId: 'notificationTimeline.card.cancelNotification.success',
        titleId: 'notificationTimeline.card.cancelNotification.confirm.title',
        bodyId: 'notificationTimeline.card.cancelNotification.confirm.body',
      })
    },
    [withConfirmation, withRefreshContent],
  )

  const cancelExpiration = useCallback(
    (notification) => {
      withConfirmation({
        effectFunc: () =>
          withRefreshContent(
            notification,
            effects.apis.cancelExpiration(R.prop('id', notification)),
          ),
        successMsgId: 'notificationTimeline.card.cancelExpiration.success',
        titleId: 'notificationTimeline.card.cancelExpiration.confirm.title',
        bodyId: 'notificationTimeline.card.cancelExpiration.confirm.body',
      })
    },
    [withConfirmation, withRefreshContent],
  )

  const getApi = useMemo(
    () => ({
      cancelReminder,
      unsend,
      cancelScheduleNotification,
      withRefreshContent,
      cancelExpiration,
    }),
    [
      cancelReminder,
      unsend,
      cancelScheduleNotification,
      withRefreshContent,
      cancelExpiration,
    ],
  )

  return <NotificationAction.Provider {...rest} value={getApi} />
}

export const NotificationAction = createContext(null)
export default NotificationActionProvider
